import { type FC, useEffect, useState } from 'react';
import { Form } from 'antd';
import { Modal, Select } from '@g2u/ui-kit';

import { validateFormSilently } from 'helpers';
import { ServiceTypeAvailability, TechnicianServiceType } from 'types';

const NO_SERVICE_TYPES_AVAILABLE_VALUE = '';

const serviceTypeStatusOptions = [
  { value: ServiceTypeAvailability.NotAvailable.toString(), label: 'Not Available' },
  { value: ServiceTypeAvailability.Available.toString(), label: 'Available' },
];

type Props = {
  serviceType: TechnicianServiceType | null;
  assignableServiceTypes: TechnicianServiceType[];
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (values: ServiceTypeModal.FormValues) => void;
  onClose: () => void;
};

export const ServiceTypeModal: FC<Props> = (props) => {
  const { serviceType, assignableServiceTypes, isOpen, isLoading, onSubmit, onClose } = props;
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (isOpen) {
      validateFormSilently(form, setIsFormValid);
    }
  }, [isOpen, form]);

  const handleFormChange = () => {
    validateFormSilently(form, setIsFormValid);
  };

  const handleFormSubmit = (values: ServiceTypeModal.FormValues) => {
    onSubmit({
      technicianServiceTypeId: serviceType?.ID,
      service_type_ID: Number(values.service_type_ID),
      status: Number(values.status) as ServiceTypeAvailability,
    });
  };

  const hasAssignableServiceTypes = assignableServiceTypes.length > 0;

  const serviceTypeOptions = serviceType
    ? [{ value: serviceType.service_type_ID.toString(), label: serviceType.type_description }]
    : hasAssignableServiceTypes
      ? assignableServiceTypes.map((assignable) => ({
          value: assignable.service_type_ID.toString(),
          label: assignable.type_description,
        }))
      : [{ value: NO_SERVICE_TYPES_AVAILABLE_VALUE, label: 'No Service Types Available', disabled: true }];

  const serviceTypeInitialValue = serviceType
    ? serviceType.service_type_ID.toString()
    : hasAssignableServiceTypes
      ? undefined
      : NO_SERVICE_TYPES_AVAILABLE_VALUE;

  return (
    <Modal.$
      dataTestId="technician-service-type-modal"
      open={isOpen}
      title={serviceType ? 'Edit Service Type' : 'New Service Type'}
      okText="Save"
      onCancel={onClose}
      onOk={form.submit}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{ disabled: !isFormValid || isLoading }}
    >
      <Form
        form={form}
        onFinish={handleFormSubmit}
        onValuesChange={handleFormChange}
        labelCol={{ style: { width: 130 } }}
        labelAlign="left"
        layout="horizontal"
        disabled={isLoading}
        preserve={false}
      >
        <Select.$
          name="service_type_ID"
          label="Service Type"
          rules={[{ required: true, message: 'Service Type is required.' }]}
          required
          disabled={!!serviceType}
          initialValue={serviceTypeInitialValue}
          options={serviceTypeOptions}
        />
        <Select.$
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Status is required.' }]}
          required
          initialValue={(serviceType?.service_status ?? ServiceTypeAvailability.NotAvailable).toString()}
          options={serviceTypeStatusOptions}
        />
      </Form>
    </Modal.$>
  );
};

export namespace ServiceTypeModal {
  export type FormValues = {
    service_type_ID: number;
    status: ServiceTypeAvailability;
    technicianServiceTypeId?: number;
  };
}
